import { useState, useCallback } from 'react';
import { doctorShiftApi } from '../services/doctorShiftApiService';
import { doctorApiService } from '../services/doctorApiService';
import { 
  DOCTOR_SHIFT_CSV_HEADERS, 
  HEADER_FIELD_MAPPING,
  CSV_FILE_CONFIG 
} from '../constants/doctorShiftHeaders';
import type { 
  DoctorShiftRecord, 
  DoctorShiftUploadState,
  ValidationError,
  UploadResult
} from '../types/doctorShift';
import type { Doctor } from '../types/doctor';

/**
 * 医師シフトCSVアップロードカスタムフック
 * 
 * 意図：
 * - CSVファイルのアップロード処理を管理
 * - バリデーションとエラーハンドリング
 * - アップロード進捗の追跡
 * 
 * 注意点：
 * - タブ区切りCSVファイルを想定
 * - 最大5MBまでのファイルサイズ制限
 * - エンコーディング自動検出（UTF-8/Shift-JIS）
 */
export const useDoctorShiftCsvUploader = () => {
  const [state, setState] = useState<DoctorShiftUploadState>({
    file: null,
    isUploading: false,
    uploadProgress: 0,
    error: null,
    validationErrors: [],
    previewData: [],
    uploadResult: null
  });

  /**
   * エンコーディングを検出
   */
  const detectEncoding = async (file: File): Promise<'utf-8' | 'shift-jis'> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        // 簡易的な判定：日本語が文字化けしていればShift-JIS
        if (text.includes('�') || text.match(/[\ufffd]/)) {
          resolve('shift-jis');
        } else {
          resolve('utf-8');
        }
      };
      reader.readAsText(file.slice(0, 1024)); // 最初の1KBで判定
    });
  };

  /**
   * ファイルを読み込む
   */
  const readFile = async (file: File, encoding: 'utf-8' | 'shift-jis'): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      
      if (encoding === 'shift-jis') {
        reader.readAsText(file, 'Shift_JIS');
      } else {
        reader.readAsText(file, 'UTF-8');
      }
    });
  };

  /**
   * タブ区切りCSVをパース（独自実装）
   */
  const parseCsv = (csvText: string): { data: string[][]; errors: any[] } => {
    try {
      const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');
      const data = lines.map(line => line.split('\t'));
      
      // 基本的なバリデーション
      const errors: any[] = [];
      if (data.length === 0) {
        errors.push({ message: 'CSVファイルが空です', row: 0 });
      }
      
      // 各行の列数チェック
      if (data.length > 0) {
        const headerLength = data[0].length;
        data.forEach((row, index) => {
          if (row.length !== headerLength) {
            errors.push({ 
              message: `列数が一致しません（期待値: ${headerLength}, 実際: ${row.length}）`, 
              row: index + 1 
            });
          }
        });
      }
      
      return { data, errors };
    } catch (error) {
      return { 
        data: [], 
        errors: [{ message: 'CSVの解析に失敗しました', row: 0 }] 
      };
    }
  };

  /**
   * CSVデータを医師シフトレコードに変換
   */
  const convertToRecords = (data: string[][]): DoctorShiftRecord[] => {
    const [headers, ...rows] = data;
    
    // ヘッダーのインデックスマップを作成
    const headerIndexMap = new Map<string, number>();
    headers.forEach((header, index) => {
      const trimmedHeader = header.trim();
      if (DOCTOR_SHIFT_CSV_HEADERS.includes(trimmedHeader as any)) {
        headerIndexMap.set(trimmedHeader, index);
      }
    });

    // 各行をレコードに変換
    return rows.map(row => {
      const record: Partial<DoctorShiftRecord> = {};
      
      DOCTOR_SHIFT_CSV_HEADERS.forEach(header => {
        const index = headerIndexMap.get(header);
        if (index !== undefined) {
          const fieldName = HEADER_FIELD_MAPPING[header];
          record[fieldName] = row[index]?.trim() || '';
        }
      });

      return record as DoctorShiftRecord;
    });
  };

  /**
   * CSVヘッダーを検証
   */
  const validateHeaders = (headers: string[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    const trimmedHeaders = headers.map(h => h.trim());

    DOCTOR_SHIFT_CSV_HEADERS.forEach(expectedHeader => {
      if (!trimmedHeaders.includes(expectedHeader)) {
        errors.push({
          row: 1,
          field: 'ヘッダー',
          message: `必須列「${expectedHeader}」が見つかりません`
        });
      }
    });

    return errors;
  };

  /**
   * ファイル選択ハンドラー
   */
  const handleFileSelect = useCallback(async (file: File | null) => {
    if (!file) {
      setState(prev => ({
        ...prev,
        file: null,
        error: null,
        validationErrors: [],
        previewData: [],
        uploadResult: null
      }));
      return;
    }

    // ファイルサイズチェック
    if (file.size > CSV_FILE_CONFIG.maxSizeInBytes) {
      setState(prev => ({
        ...prev,
        file: null,
        error: `ファイルサイズが${CSV_FILE_CONFIG.maxSizeInMB}MBを超えています`,
        validationErrors: [],
        previewData: []
      }));
      return;
    }

    setState(prev => ({ ...prev, file, error: null, validationErrors: [] }));

    try {
      // エンコーディング検出
      const encoding = await detectEncoding(file);
      
      // ファイル読み込み
      const fileContent = await readFile(file, encoding);
      
      // CSVパース
      const parseResult = parseCsv(fileContent);
      
      if (parseResult.errors.length > 0) {
        setState(prev => ({
          ...prev,
          error: 'CSVファイルの解析に失敗しました',
          validationErrors: parseResult.errors.map((err: any, index: number) => ({
            row: err.row || index + 1,
            field: 'パース',
            message: err.message
          }))
        }));
        return;
      }

      const data = parseResult.data;
      
      // ヘッダー検証
      const headerErrors = validateHeaders(data[0]);
      if (headerErrors.length > 0) {
        setState(prev => ({
          ...prev,
          validationErrors: headerErrors,
          error: 'CSVヘッダーにエラーがあります'
        }));
        return;
      }

      // データ変換
      const records = convertToRecords(data);
      
      // 医師IDのユニークリストを取得
      const uniqueDoctorIds = Array.from(new Set(records.map(r => r.doctor_id)));
      
      // 医師情報を取得してマッピング（バックエンドの100件制限に対応）
      let doctorMap: Map<string, Doctor> = new Map();
      if (uniqueDoctorIds.length > 0) {
        try {
          let currentPage = 1;
          let hasMorePages = true;
          const maxPerPage = 100; // バックエンドの最大件数
          
          // 必要に応じて複数ページを取得
          while (hasMorePages) {
            const doctorsResponse = await doctorApiService.getDoctors({
              page: currentPage,
              per_page: maxPerPage
            });
            
            // 医師IDと医師情報のマッピングを作成
            doctorsResponse.data.forEach(doctor => {
              doctorMap.set(doctor.doctor_code, doctor);
            });
            
            // 次のページがあるか確認
            if (doctorsResponse.meta && doctorsResponse.meta.current_page < doctorsResponse.meta.last_page) {
              currentPage++;
            } else {
              hasMorePages = false;
            }
            
            // 必要な医師情報がすべて取得できたら終了
            const foundDoctorIds = Array.from(doctorMap.keys());
            const allDoctorsFound = uniqueDoctorIds.every(id => foundDoctorIds.includes(id));
            if (allDoctorsFound) {
              hasMorePages = false;
            }
          }
        } catch (doctorError) {
          console.error('医師情報の取得に失敗しました:', doctorError);
        }
      }

      // プレビューデータに医師名を追加
      const enrichedRecords = records.map(record => {
        const doctor = doctorMap.get(record.doctor_id);
        return {
          ...record,
          doctor_full_name: doctor ? `${doctor.first_name}${doctor.last_name}先生` : undefined
        } as DoctorShiftRecord & { doctor_full_name?: string };
      });
      
      // データバリデーション
      const validationResult = doctorShiftApi.validateCsvData(records);
      
      setState(prev => ({
        ...prev,
        previewData: enrichedRecords as any,
        validationErrors: validationResult.errors,
        error: validationResult.isValid ? null : 'データにエラーがあります'
      }));

    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'ファイルの読み込みに失敗しました',
        validationErrors: []
      }));
    }
  }, []);

  /**
   * アップロード実行
   */
  const uploadFile = useCallback(async (): Promise<void> => {
    if (!state.file || state.validationErrors.length > 0) {
      return;
    }

    setState(prev => ({ 
      ...prev, 
      isUploading: true, 
      uploadProgress: 0,
      uploadResult: null 
    }));

    try {
      // エンコーディング検出
      const encoding = await detectEncoding(state.file);
      
      // ファイル読み込み
      const fileContent = await readFile(state.file, encoding);
      
      // アップロード開始
      setState(prev => ({ ...prev, uploadProgress: 30 }));
      
      // API呼び出し
      const response = await doctorShiftApi.uploadCsv(fileContent, encoding);
      
      console.log('📘 アップロードAPIレスポンス:', response);
      
      setState(prev => ({ ...prev, uploadProgress: 60 }));

      // ステータス確認（処理中の場合はポーリング）
      let status = response;
      while (status.status === 'processing') {
        await new Promise(resolve => setTimeout(resolve, 1000));
        status = await doctorShiftApi.getUploadStatus(status.id);
        setState(prev => ({ 
          ...prev, 
          uploadProgress: Math.min(90, prev.uploadProgress + 5) 
        }));
      }

      // 完了
      const result: UploadResult = {
        success: status.status === 'completed',
        message: status.message || 'アップロードが完了しました',
        processedCount: status.processedCount,
        errorCount: status.errors?.length || 0,
        errors: status.errors
      };
      
      console.log('📘 アップロード結果:', { status: status.status, result });

      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadProgress: 100,
        uploadResult: result,
        error: result.success ? null : result.message
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'アップロードに失敗しました';
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadProgress: 0,
        error: errorMessage,
        uploadResult: {
          success: false,
          message: errorMessage
        }
      }));
    }
  }, [state.file, state.validationErrors]);

  /**
   * リセット
   */
  const reset = useCallback(() => {
    setState({
      file: null,
      isUploading: false,
      uploadProgress: 0,
      error: null,
      validationErrors: [],
      previewData: [],
      uploadResult: null
    });
  }, []);

  return {
    ...state,
    handleFileSelect,
    uploadFile,
    reset
  };
};